import 'abortcontroller-polyfill/dist/polyfill-patch-fetch';
import { AjaxAction, AjaxActionType, createAjaxActionStart, createAjaxActionEnd, AjaxActionPayload, AjaxResponse } from '../_actions/AjaxAction';
import { MiddlewareAPI, Dispatch, Middleware } from "redux";
import RateLimiter from '../Utils/RateLimiter';
import WebsiteStore, { IWebsiteStore } from '../../WebsiteStore';
import { ResetApiErrors, Toast } from '../../UIData/_actions/UIDataActions';
import isAbortError from '../Utils/isAbortError';
import { ShowSessionMismatchDialog, type SessionMismatchReason } from '../../Auth/_actions/AuthActions';

// Rate Limiters & Abort Controllers
let tracker:string[] = [];
let rateLimiters:{[key:string]:RateLimiter} = {};
let abortControllers:{[key:string]:AbortController} = {};

const getSessionMismatchReason = (response: unknown): SessionMismatchReason | undefined => {
	const data = (response as { data?: { error?: string, reason?: string } } | undefined)?.data;

	if(data?.error !== 'session-mismatch') return undefined;
	if(data.reason === 'different-user') return 'different-user';
	return 'logged-out';
}

const AjaxMiddleware: Middleware<Dispatch, WebsiteStore> = ({dispatch, getState}: MiddlewareAPI) => next => (action: AjaxAction<unknown>) => {
	let {
		url,
		method = "GET",
		credentials = 'include',
		data,
		limit,
		abortPrevious,
		preventDuplicates,
		onSuccess,
		onFailure,
		onAbort,
		raw,
		headers = {},
		currencyHeader = true,
	} = action;
	const {type, actionType, ...payload} = action;

	if (type != AjaxActionType) return next(action);

	const controller = abortPrevious ? new AbortController() : undefined;

	const store = getState() as IWebsiteStore;
	const locale = store.userData.get('prefs').get('lang').substring(0,2);

	//Prepend locale
	if(url.indexOf('ajax/') === 0 && locale !== 'en') {
		//console.log(url, store.userData.get('prefs').get('lang').substring(0,2), 'ajax/'+store.userData.get('prefs').get('lang').substring(0,2)+url.substring(4));
		url = 'ajax/'+store.userData.get('prefs').get('lang').substring(0,2)+url.substring(4)
	}

	const baseUrl = url;

	let callable = async () => {
		dispatch(ResetApiErrors())
		dispatch(createAjaxActionStart(actionType, payload as AjaxActionPayload));
		let shouldDispatchEnd = true;

		const requestHeaders: Record<string, string> = {};

		Object.entries(headers).forEach(([key, value]) => {
			if(value !== undefined && value !== null) {
				requestHeaders[key] = String(value);
			}
		});

		const currentStore = getState() as IWebsiteStore;
		const userData = currentStore.userData;
		const userId = Number(userData.get('id') ?? 0);
		const loggedIn = Boolean(userData.get('loggedIn'));

		if(loggedIn) {
			requestHeaders['X-AOW-Frontend-Logged-In'] = '1';
			requestHeaders['X-AOW-Frontend-User-ID'] = String(userId);
		}

		const currency = currentStore.userData.get('prefs').get('currency');
		if(currencyHeader && ['CAD', 'USD'].includes(currency)) {
			requestHeaders['X-App-Currency'] = currency;
		}

		const options:RequestInit = {
			method,
			credentials
		};

		if(Object.keys(requestHeaders).length > 0) {
			options.headers = requestHeaders;
		}

		if(controller !== undefined) {
			options.signal = controller.signal;
		}

		if(data){
			if(method == "GET"){
				let values:string[][] = [];

				if(data instanceof FormData) {
					data.forEach((v:string, k:string) => values.push([k, v]));
				} else {
					Object.entries(data).forEach(([k, v]) => values.push([k, String(v)]));
				}

				url += (url.includes('?') ? '&' : '?') + new URLSearchParams(values);
			} else if (method == "POST") {
				options.body = data;
			}
		}

		let end = () => dispatch(createAjaxActionEnd(actionType, payload as AjaxActionPayload));

		return fetch(url, options)
			.then(response => { 
				if(response.type != "basic") raw = true;
				return response.json()
			})
			.then(response => {
				if(!raw && response.code !== 200) throw response;

				//Check for flash data
				if(response.flash) {
					for(let key in response.flash) {
						if(response.flash[key].type === 'goal') {
							dispatch(Toast('', {
								id_goal: response.flash[key].data.id_goal,
								count: response.flash[key].data.count,
								total: response.flash[key].data.total,
							}))
						}
					}
				}

				onSuccess && onSuccess(response as AjaxResponse, dispatch)
			})
			.catch(response => {
				if(isAbortError(response)) {
					shouldDispatchEnd = !abortPrevious || (controller !== undefined && abortControllers[baseUrl] === controller);
					return onAbort && onAbort(response as AjaxResponse<unknown>, dispatch);
				}

				const sessionMismatchReason = getSessionMismatchReason(response);
				if(sessionMismatchReason !== undefined) {
					dispatch(ShowSessionMismatchDialog(sessionMismatchReason));
					return;
				}

				return onFailure && onFailure(response as AjaxResponse, dispatch);
			})
			.finally(() => {
				if (shouldDispatchEnd) {
					end();
				}

				if(abortPrevious && abortControllers[baseUrl] === controller) {
					delete abortControllers[baseUrl];
				}

				if(preventDuplicates && (tracker.indexOf(baseUrl) !== -1)){
					tracker.splice(tracker.indexOf(baseUrl), 1);
				}
			})
	}

	if(preventDuplicates){
		if(tracker.indexOf(baseUrl) !== -1) return
		tracker.push(baseUrl);
	}

	if(controller !== undefined) {
		if(abortControllers[baseUrl]) abortControllers[baseUrl].abort();
		abortControllers[baseUrl] = controller;
	}

	if(limit) {
		if(!rateLimiters[baseUrl]) rateLimiters[baseUrl] = new RateLimiter(limit);
		rateLimiters[baseUrl].throttle(callable);
	} else return callable();
};

export default AjaxMiddleware;
